// Card statement reconciliation — pure logic (2026-07-12).
//
// Parses a monthly card statement CSV (Amex export shapes first) and
// cross-checks every charge against recorded receipts. NO AI anywhere —
// the file is structured data and the matching is deterministic:
// amount + date window first, vendor-name/alias disambiguation second.
//
// Lives in src/lib (not utils.js) as a self-contained module, same
// precedent as vendorDualWrite.js / receiptCopy.js. Consumed by
// CardStatementsSection.jsx; guard tests in
// src/__tests__/cardStatements.test.js.

import { resolveVendorByName, fmtMoney } from '../utils';

// ── CSV parsing ────────────────────────────────────────────────────────

// Split one CSV line honoring double-quoted fields ("" escapes a quote).
export function splitCsvLine(line) {
  var out = [], cur = '', inQ = false;
  for (var i = 0; i < line.length; i++) {
    var ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQ = true;
    } else if (ch === ',') {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

// 'MM/DD/YYYY', 'MM/DD/YY', 'YYYY-MM-DD' (optionally with trailing
// time) → 'YYYY-MM-DD'. Returns '' when unparseable.
export function normalizeStatementDate(s) {
  var v = String(s == null ? '' : s).trim().split(/[T ]/)[0];
  function build(yy, mo, dd) {
    var mn = parseInt(mo, 10), dn = parseInt(dd, 10);
    if (mn < 1 || mn > 12 || dn < 1 || dn > 31) return '';
    return yy + '-' + String(mn).padStart(2, '0') + '-' + String(dn).padStart(2, '0');
  }
  var m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) return build(m[3].length === 2 ? '20' + m[3] : m[3], m[1], m[2]);
  m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return build(m[1], m[2], m[3]);
  return '';
}

// '$1,234.56', '(45.00)', '-45.00', '1234.5' → number. NaN when not an
// amount. Parentheses mean negative (accounting convention).
export function parseStatementAmount(s) {
  var v = String(s == null ? '' : s).trim();
  if (!v) return NaN;
  var neg = false;
  if (/^\(.*\)$/.test(v)) { neg = true; v = v.slice(1, -1); }
  v = v.replace(/[$,\s]/g, '').replace(/−/g, '-');
  if (!/^-?\d*\.?\d+$/.test(v)) return NaN;
  var n = parseFloat(v);
  return neg ? -n : n;
}

// Parse a whole statement CSV. Handles a header row (finds Date /
// Description / Amount columns by name — the extended Amex export with
// Card Member / Account # columns just works) or headerless Amex shape
// (date, descriptor, ..., amount-last). Amex sign convention: charges
// positive, payments/credits negative — kept as-is, isCredit derived.
//
// Returns { lines, errors, headerDetected, dateMin, dateMax,
//           chargeCount, chargeTotal, creditCount, creditTotal }.
// lines: [{ lineDate, descriptor, amount, isCredit }]
// errors: [{ lineNumber, text, reason }] — skipped rows, never fatal.
export function parseCardStatementCsv(text) {
  var res = {
    lines: [], errors: [], headerDetected: false,
    dateMin: '', dateMax: '',
    chargeCount: 0, chargeTotal: 0, creditCount: 0, creditTotal: 0,
  };
  var raw = String(text == null ? '' : text).replace(/^﻿/, '');
  var rows = raw.split(/\r\n|\n|\r/);

  var dateIdx = 0, descIdx = 1, amtIdx = -1;   // -1 = last cell
  var startAt = 0;

  // Header detection on the first non-empty row. Column names vary by
  // bank: Amex says 'Date', Chase checking says 'Posting Date', Chase
  // cards say 'Transaction Date' (with 'Post Date' beside it — findIndex
  // takes the first, which is the one we want: the day the money moved).
  // Match on CONTAINS, not starts-with (the 2026-07-19 Chase fix — the
  // old prefix match fell through to headerless mode and every row
  // errored, so uploads looked like nothing happened).
  for (var h = 0; h < rows.length; h++) {
    if (!rows[h].trim()) continue;
    var cells = splitCsvLine(rows[h]).map(function(c) { return c.trim().toLowerCase(); });
    var di = cells.findIndex(function(c) { return c.indexOf('date') >= 0; });
    var xi = cells.findIndex(function(c) { return c.indexOf('desc') >= 0 || c === 'payee' || c === 'merchant' || c === 'name'; });
    var ai = cells.findIndex(function(c) { return c === 'amount' || c.indexOf('amount') >= 0; });
    if (di >= 0 && xi >= 0 && ai >= 0) {
      res.headerDetected = true;
      dateIdx = di; descIdx = xi; amtIdx = ai;
      startAt = h + 1;
    } else {
      startAt = h;   // headerless — first non-empty row is data
    }
    break;
  }

  for (var i = startAt; i < rows.length; i++) {
    var line = rows[i];
    if (!line.trim()) continue;
    var cs = splitCsvLine(line);
    var dateRaw = cs[dateIdx];
    var amtRaw  = amtIdx === -1 ? cs[cs.length - 1] : cs[amtIdx];
    var desc    = String(cs[descIdx] == null ? '' : cs[descIdx]).trim();

    var lineDate = normalizeStatementDate(dateRaw);
    var amount   = parseStatementAmount(amtRaw);
    if (!lineDate || isNaN(amount)) {
      res.errors.push({
        lineNumber: i + 1, text: line.slice(0, 120),
        reason: !lineDate ? 'unreadable date' : 'unreadable amount',
      });
      continue;
    }
    var isCredit = amount < 0;
    res.lines.push({ lineDate: lineDate, descriptor: desc, amount: amount, isCredit: isCredit });
    if (isCredit) { res.creditCount++; res.creditTotal += amount; }
    else          { res.chargeCount++; res.chargeTotal += amount; }
    if (!res.dateMin || lineDate < res.dateMin) res.dateMin = lineDate;
    if (!res.dateMax || lineDate > res.dateMax) res.dateMax = lineDate;
  }
  res.chargeTotal = Math.round(res.chargeTotal * 100) / 100;
  res.creditTotal = Math.round(res.creditTotal * 100) / 100;
  return res;
}

// Split a parsed statement into calendar-month groups (2026-07-19 —
// Cimon: "upload the statements and gilda automatically put them in by
// month... would help align the different statement dates"). Card
// cycles run mid-month to mid-month; bucketing by each line's OWN date
// aligns every account on the same calendar months. Returns groups
// newest-first: [{ period: 'YYYY-MM', lines, chargeCount, chargeTotal,
// creditCount, creditTotal }].
export function splitStatementByMonth(parsed) {
  var by = {};
  (((parsed && parsed.lines) || [])).forEach(function(l) {
    if (!l || !l.lineDate) return;
    var period = String(l.lineDate).slice(0, 7);
    (by[period] = by[period] || []).push(l);
  });
  return Object.keys(by).sort().reverse().map(function(period) {
    var lines = by[period];
    var g = { period: period, lines: lines,
              chargeCount: 0, chargeTotal: 0, creditCount: 0, creditTotal: 0 };
    lines.forEach(function(l) {
      if (l.isCredit) { g.creditCount++; g.creditTotal += l.amount; }
      else            { g.chargeCount++; g.chargeTotal += l.amount; }
    });
    g.chargeTotal = Math.round(g.chargeTotal * 100) / 100;
    g.creditTotal = Math.round(g.creditTotal * 100) / 100;
    return g;
  });
}

// Some sources (bank checking accounts, some card exports) record
// spending as NEGATIVE and payments as positive — the mirror of Amex's
// convention. Detect by majority: more negative lines than positive →
// probably spend-negative.
export function detectSpendNegative(parsed) {
  var lines = (parsed && parsed.lines) || [];
  var neg = 0, pos = 0;
  lines.forEach(function(l) { if (l.amount < 0) neg++; else if (l.amount > 0) pos++; });
  return neg > pos;
}

// Flip every line's sign and recompute the roll-ups. Pure — returns a
// new parsed object; the caller keeps the original for un-flipping.
export function flipStatementSigns(parsed) {
  var out = {
    lines: [], errors: (parsed && parsed.errors) || [],
    headerDetected: !!(parsed && parsed.headerDetected),
    dateMin: (parsed && parsed.dateMin) || '', dateMax: (parsed && parsed.dateMax) || '',
    chargeCount: 0, chargeTotal: 0, creditCount: 0, creditTotal: 0,
  };
  ((parsed && parsed.lines) || []).forEach(function(l) {
    var amount = -l.amount;
    var isCredit = amount < 0;
    out.lines.push({ lineDate: l.lineDate, descriptor: l.descriptor, amount: amount, isCredit: isCredit });
    if (isCredit) { out.creditCount++; out.creditTotal += amount; }
    else          { out.chargeCount++; out.chargeTotal += amount; }
  });
  out.chargeTotal = Math.round(out.chargeTotal * 100) / 100;
  out.creditTotal = Math.round(out.creditTotal * 100) / 100;
  return out;
}

// ── Charge classification (the "list for Dan") ───────────────────────
// A charge is either PROJECT spend (assign a PO — the receipt link does
// this automatically) or one of these firm-side buckets. Mirrors the
// receipt-kind language so the two vocabularies stay aligned.
// Order here IS the display order (dropdown, Dan's email sections,
// spreadsheet grouping). The legacy 'office' key keeps its meaning as
// the general office bucket so pre-split classifications stay valid —
// never rename a key that has data behind it.
export var CARD_LINE_CATEGORIES = [
  { key: 'reimbursable',    label: 'Reimbursable (bill to client)' },
  { key: 'office_software', label: 'Office — software & subscriptions' },
  { key: 'office_rent',     label: 'Office — rent & utilities' },
  { key: 'office',          label: 'Office — general & supplies' },
  { key: 'marketing',       label: 'Marketing & client development' },
  { key: 'travel',          label: 'Business travel & trade shows' },
  { key: 'professional',    label: 'Professional services (legal, accounting, insurance)' },
  { key: 'payroll',         label: 'Payroll & employee benefits' },
  { key: 'taxes',           label: 'Taxes & licenses' },
  { key: 'loan',            label: 'Loan payments' },
  { key: 'personal',        label: 'Distribution (owner draw)' },   // key stays 'personal' — data behind it
  { key: 'fees',            label: 'Bank & card fees / interest' },
];

export function cardLineCategoryLabel(key) {
  var hit = CARD_LINE_CATEGORIES.find(function(c) { return c.key === key; });
  return hit ? hit.label : (key || '');
}

// A line is RESOLVED when someone (or the matcher) has said what it is:
// matched to a receipt, assigned a PO, classified, or dismissed.
// Unresolved lines are the "needs attention" pile.
export function cardLineIsResolved(l) {
  if (!l) return false;
  if (l.matchStatus === 'matched' || l.matchStatus === 'dismissed' || l.matchStatus === 'credit') return true;
  return !!(l.poId || l.category);
}

// ── Matching ───────────────────────────────────────────────────────────

var DATE_WINDOW_DAYS = 7;      // charge date vs receipt date tolerance
var AMOUNT_TOLERANCE = 0.005;  // exact to the cent

function daysApart(isoA, isoB) {
  var a = String(isoA || '').split('-'), b = String(isoB || '').split('-');
  if (a.length !== 3 || b.length !== 3) return Infinity;
  var ta = Date.UTC(+a[0], +a[1] - 1, +a[2]);
  var tb = Date.UTC(+b[0], +b[1] - 1, +b[2]);
  if (isNaN(ta) || isNaN(tb)) return Infinity;
  return Math.abs(ta - tb) / 86400000;
}

function normText(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

// Does the statement descriptor plausibly name this receipt's vendor?
// Checks the receipt's vendor string, plus the resolved directory
// vendor's canonical name and aliases (DBAs, processor names). A hit is
// the full normalized name appearing in the descriptor, or any
// significant token (≥ 4 chars) of it. Used only to DISAMBIGUATE among
// amount+date candidates — never as the sole match basis, so loose
// tokens are safe.
export function descriptorMatchesVendor(descriptor, receiptVendorName, vendors) {
  var d = normText(descriptor);
  if (!d) return false;
  var names = [receiptVendorName];
  var v = resolveVendorByName(receiptVendorName, vendors || []);
  if (v) {
    names.push(v.name);
    (v.aliases || []).forEach(function(a) { names.push(a); });
  }
  for (var i = 0; i < names.length; i++) {
    var n = normText(names[i]);
    if (!n) continue;
    if (d.indexOf(n) >= 0) return true;
    var toks = n.split(' ').filter(function(t) { return t.length >= 4; });
    for (var j = 0; j < toks.length; j++) {
      if (d.indexOf(toks[j]) >= 0) return true;
    }
  }
  return false;
}

function amountMatchesReceipt(lineAmount, rc) {
  var total = parseFloat(rc.totalAmount) || 0;
  if (total <= 0) return false;
  var ccFee = parseFloat(rc.ccFee) || 0;
  if (Math.abs(lineAmount - total) < AMOUNT_TOLERANCE) return true;
  // Vendor surcharged the card: bank charge = invoice + CC fee.
  if (ccFee > 0 && Math.abs(lineAmount - (total + ccFee)) < AMOUNT_TOLERANCE) return true;
  return false;
}

// Match statement lines against receipts. Deterministic, greedy in line
// order, each receipt consumed at most once.
//
// Per line → one of:
//   { status: 'credit' }                                (amount ≤ 0)
//   { status: 'matched', receiptId, method }            (single winner)
//   { status: 'ambiguous', candidateIds }               (2+ equal candidates)
//   { status: 'unmatched' }
// method: 'amount+date' | 'amount+date+vendor'
//
// refs: { receipts, vendors }. Rejected receipts never participate.
export function matchCardStatementLines(lines, refs) {
  var receipts = ((refs && refs.receipts) || []).filter(function(r) {
    return r && r.id && r.status !== 'rejected';
  });
  var vendors = (refs && refs.vendors) || [];
  var used = {};
  var usedPayments = {};

  return (lines || []).map(function(line) {
    if (!line || !(line.amount > 0)) return { status: 'credit' };

    var candidates = receipts.filter(function(rc) {
      return !used[rc.id]
        && amountMatchesReceipt(line.amount, rc)
        && daysApart(line.lineDate, rc.receiptDate) <= DATE_WINDOW_DAYS;
    });

    if (candidates.length === 1) {
      used[candidates[0].id] = true;
      return { status: 'matched', receiptId: candidates[0].id, method: 'amount+date' };
    }
    if (candidates.length > 1) {
      var byVendor = candidates.filter(function(rc) {
        return descriptorMatchesVendor(line.descriptor, rc.vendorName, vendors);
      });
      if (byVendor.length === 1) {
        used[byVendor[0].id] = true;
        return { status: 'matched', receiptId: byVendor[0].id, method: 'amount+date+vendor' };
      }
      return {
        status: 'ambiguous',
        candidateIds: candidates.map(function(rc) { return rc.id; }),
      };
    }

    // Second pass (2026-07-19): vendor payments recorded WITHOUT a
    // receipt file (the manual Record Payment path) still name a PO —
    // match the wire against the payment ledger so the line resolves
    // to its PO ref. Fee rows excluded (a bank line is principal).
    // State rows may be RAW snake_case — read both shapes.
    var vps = ((refs && refs.vendorPayments) || []).filter(function(vp) {
      if (!vp || !vp.id) return false;
      return (vp.paymentType || vp.payment_type || '') !== 'Fee';
    });
    var vpCandidates = vps.filter(function(vp) {
      if (usedPayments[vp.id]) return false;
      var amt = parseFloat(vp.amount) || 0;
      if (amt <= 0) return false;
      var fee = parseFloat(vp.ccFee != null ? vp.ccFee : vp.cc_fee) || 0;
      var amtHit = Math.abs(line.amount - amt) < AMOUNT_TOLERANCE
        || (fee > 0 && Math.abs(line.amount - (amt + fee)) < AMOUNT_TOLERANCE);
      if (!amtHit) return false;
      return daysApart(line.lineDate, vp.paymentDate || vp.payment_date) <= DATE_WINDOW_DAYS;
    });
    function vpResult(vp, method) {
      usedPayments[vp.id] = true;
      return { status: 'payment', paymentId: vp.id,
               poId: vp.poId || vp.po_id || null, method: method };
    }
    if (vpCandidates.length === 1) return vpResult(vpCandidates[0], 'payment amount+date');
    if (vpCandidates.length > 1) {
      var vpByVendor = vpCandidates.filter(function(vp) {
        return descriptorMatchesVendor(line.descriptor, vp.vendorName || vp.vendor_name, vendors);
      });
      if (vpByVendor.length === 1) return vpResult(vpByVendor[0], 'payment amount+date+vendor');
    }
    return { status: 'unmatched' };
  });
}

// Suggest POs for an unmatched charge: any PO whose vendor name (or a
// directory alias of it) appears in the bank descriptor. Powers the
// "suggested" group at the top of the Assign PO dropdown — the
// descriptor names the vendor far more often than the amount matches a
// receipt, so this is the workhorse for hand-assignment.
export function suggestPosForDescriptor(descriptor, purchaseOrders, vendors) {
  var d = normText(descriptor);
  if (!d) return [];
  return (purchaseOrders || []).filter(function(po) {
    if (!po || !po.id) return false;
    var vn = po.vendorName || po.vendor_name || '';
    return vn && descriptorMatchesVendor(descriptor, vn, vendors);
  });
}

// Which documents did a recorded client payment land on? DEP/FIN refs
// via the invoices table; hours + services asks labeled in words.
// Feeds the incoming-deposits rows and Dan's spreadsheet.
export function describeRecordedPaymentDocs(payment, paymentAllocations, invoices) {
  if (!payment) return '';
  var out = [];
  (paymentAllocations || []).forEach(function(a) {
    if (!a || (a.paymentId || a.payment_id) !== payment.id) return;
    var dt = a.documentType || a.document_type || '';
    var did = a.documentId || a.document_id || '';
    var label = '';
    if (dt === 'invoice') {
      var inv = (invoices || []).find(function(x) { return x && x.id === did; });
      label = inv ? (inv.invoiceRef || inv.invoice_ref || '') : '';
    } else if (dt === 'hours_invoice') {
      label = 'Hours ' + did;
    } else if (dt === 'billing_summary') {
      label = 'Services & expenses';
    }
    if (label && out.indexOf(label) < 0) out.push(label);
  });
  return out.join(' \u00b7 ');
}

// ── Month-closed state (2026-07-19) ────────────────────────────────────
// A statement is RECONCILED when every spend line is resolved (matched,
// PO-assigned, classified, or dismissed) and every credit line is
// accounted for (a recorded client payment, or a card payment/refund).
export function statementReconciliationState(lines, clientPayments) {
  var ls = lines || [];
  var spend = ls.filter(function(l) { return l && l.matchStatus !== 'credit'; });
  var credits = ls.filter(function(l) { return l && l.matchStatus === 'credit'; });
  var unresolved = spend.filter(function(l) { return !cardLineIsResolved(l); }).length;
  if (credits.length) {
    matchIncomingDeposits(credits, clientPayments || []).forEach(function(m) {
      if (m.status === 'unrecorded' || m.status === 'ambiguous') unresolved++;
    });
  }
  return { total: ls.length, unresolved: unresolved,
           closed: ls.length > 0 && unresolved === 0 };
}

// ── The list for Dan ───────────────────────────────────────────────────
// Plain-text summary of a classified statement, written in BOOKKEEPER
// language (vendor / amount / PO number / project — no Gilda jargon).
// Dan lives in QB; this is the bridge: paste into an email and every
// line tells him what the charge is and where it goes.
export function buildStatementSummaryText(statement, lines, refs) {
  var receipts = (refs && refs.receipts) || [];
  var pos      = (refs && refs.purchaseOrders) || [];
  var rById = {}, pById = {};
  receipts.forEach(function(r) { if (r && r.id) rById[r.id] = r; });
  pos.forEach(function(p) { if (p && p.id) pById[p.id] = p; });

  function money(n) { return fmtMoney(Math.abs(parseFloat(n) || 0)); }
  function chargeLine(l, suffix) {
    return '  ' + (l.lineDate || '') + '  ' + money(l.amount) + '  ' + (l.descriptor || '')
      + (suffix ? '  → ' + suffix : '')
      + (l.notes ? '  [note: ' + l.notes + ']' : '');
  }
  function poBits(poId) {
    var po = poId ? pById[poId] : null;
    if (!po) return '';
    var ref = po.po_ref || po.poRef || '';
    var proj = po.project || '';
    return ref + (proj ? ' (' + proj + ')' : '');
  }

  // Buckets derive from CARD_LINE_CATEGORIES (order + labels) so a new
  // category automatically gets its own section here.
  var project = [], review = [], credits = [];
  var byCategory = {};
  CARD_LINE_CATEGORIES.forEach(function(c) { byCategory[c.key] = []; });
  (lines || []).forEach(function(l) {
    if (l.matchStatus === 'credit') { credits.push(l); return; }
    if (l.matchStatus === 'dismissed') return;
    var rc = l.matchedReceiptId ? rById[l.matchedReceiptId] : null;
    // Matched charges classify by their receipt's kind — reimbursable
    // and Office expenses run through the receipt reader too.
    if (l.matchStatus === 'matched' && rc && rc.kind === 'project_reimbursable') {
      byCategory.reimbursable.push(chargeLine(l, 'receipt recorded — bills to the client'));
      return;
    }
    if (l.matchStatus === 'matched' && rc && rc.kind === 'personal') {
      byCategory.office.push(chargeLine(l, 'office receipt recorded'));
      return;
    }
    // Direct merchandise purchase (2026-08-23): a receipt with an item
    // link and NO PO — Dan should never go hunting for a PO that
    // doesn't exist. The client side closes via the item's FIN.
    if (l.matchStatus === 'matched' && rc && rc.kind === 'merchandise_direct') {
      project.push(chargeLine(l,
        (rc.projectLabel ? rc.projectLabel + ' — ' : '')
        + 'direct merchandise purchase (no PO) — receipt recorded; client billed on a final invoice'));
      return;
    }
    if (l.matchStatus === 'matched' || l.poId) {
      var bits = poBits(l.poId);
      var suffix = 'PO ' + (bits || 'unknown')
        + (rc ? ' — receipt recorded in Gilda' : ' — no receipt yet');
      project.push(chargeLine(l, suffix));
      return;
    }
    if (l.category === 'reimbursable') {
      // Classified reimbursable but NOT matched to a receipt = not yet
      // billable. Reimbursables always go through the receipt reader.
      byCategory.reimbursable.push(chargeLine(l, 'NEEDS RECEIPT — not billable until recorded'));
      return;
    }
    if (byCategory[l.category]) { byCategory[l.category].push(chargeLine(l)); return; }
    review.push(chargeLine(l));
  });

  var out = [];
  out.push(((statement && statement.label) || 'Card') + ' statement — '
    + ((statement && statement.period) || ''));
  function section(title, arr) {
    if (!arr.length) return;
    out.push('');
    out.push(title + ' (' + arr.length + '):');
    arr.forEach(function(s) { out.push(s); });
  }
  section('PROJECT CHARGES — assign to the PO noted', project);
  CARD_LINE_CATEGORIES.forEach(function(c) {
    section(c.label.toUpperCase(), byCategory[c.key]);
  });
  section('STILL SORTING OUT — will follow up', review);
  if (credits.length) {
    var tot = credits.reduce(function(s, l) { return s + l.amount; }, 0);
    out.push('');
    out.push('Payments/credits on the statement: ' + credits.length
      + ' totaling ' + fmtMoney(Math.abs(Math.round(tot * 100) / 100)));
  }
  return out.join('\n');
}

// ── Incoming deposits (the client-money side, 2026-07-12) ────────────
// On a bank statement the credit lines are where client wires live.
// Match each one against payments recorded through the client-payment
// wizard: one wizard run = one bank deposit at the true bank figure
// (Cimon's 2026-07-10 design), so amounts should agree to the cent and
// dates land within days. Derived LIVE — nothing persisted; recording
// a missing payment self-heals the pile on next render.
//
// Amex-style credit lines (payments TO the card, refunds) are labeled
// by descriptor so they don't read as missing client money.

export function isCardPaymentDescriptor(descriptor) {
  return /payment|thank you|autopay|epay|e-payment/i.test(String(descriptor || ''));
}

var DEPOSIT_DATE_WINDOW_DAYS = 5;

// creditLines: statement lines with matchStatus 'credit' (amounts
// negative under the charges-positive convention). clientPayments:
// ctx rows (amountTotal, paymentDate). Greedy; each payment consumed
// once. Per line → { status: 'recorded', paymentId } |
// { status: 'card_payment' } | { status: 'ambiguous', paymentIds } |
// { status: 'unrecorded' }.
export function matchIncomingDeposits(creditLines, clientPayments) {
  var payments = (clientPayments || []).filter(function(p) { return p && p.id; });
  var used = {};
  return (creditLines || []).map(function(line) {
    var amt = Math.abs(parseFloat(line && line.amount) || 0);
    if (!amt) return { status: 'card_payment' };
    var candidates = payments.filter(function(p) {
      return !used[p.id]
        && Math.abs((parseFloat(p.amountTotal) || 0) - amt) < AMOUNT_TOLERANCE
        && daysApart(line.lineDate, p.paymentDate) <= DEPOSIT_DATE_WINDOW_DAYS;
    });
    if (candidates.length === 1) {
      used[candidates[0].id] = true;
      return { status: 'recorded', paymentId: candidates[0].id };
    }
    if (candidates.length > 1) {
      return { status: 'ambiguous', paymentIds: candidates.map(function(p) { return p.id; }) };
    }
    if (isCardPaymentDescriptor(line.descriptor)) return { status: 'card_payment' };
    return { status: 'unrecorded' };
  });
}

// Who does an unrecorded wire look like it came from? Client names and
// project labels checked against the bank descriptor (wires usually
// carry the sender's name). Returns a display string or ''.
export function suggestDepositSource(descriptor, clients, projects) {
  var d = normText(descriptor);
  if (!d) return '';
  var names = [];
  (clients || []).forEach(function(c) { if (c && c.name) names.push(c.name); });
  (projects || []).forEach(function(p) {
    if (p && p.displayName) names.push(p.displayName);
    if (p && p.label) names.push(p.label);
  });
  for (var i = 0; i < names.length; i++) {
    var n = normText(names[i]);
    if (!n) continue;
    var toks = n.split(' ').filter(function(t) { return t.length >= 4; });
    for (var j = 0; j < toks.length; j++) {
      if (d.indexOf(toks[j]) >= 0) return names[i];
    }
  }
  return '';
}

// One-line description of a recorded payment for the matched-deposit
// row: project, date, method, and how many documents the wizard placed
// it on.
export function describeRecordedPayment(payment, paymentAllocations) {
  if (!payment) return '';
  var n = (paymentAllocations || []).filter(function(a) {
    return a && (a.paymentId || a.payment_id) === payment.id;
  }).length;
  var bits = [];
  if (payment.projectLabel) bits.push(payment.projectLabel);
  if (payment.paymentDate) bits.push(payment.paymentDate);
  if (payment.method) bits.push(payment.method);
  if (n) bits.push('placed on ' + n + ' document' + (n === 1 ? '' : 's'));
  return bits.join(' · ');
}

// ── Dan's spreadsheet ──────────────────────────────────────────────────
// Excel is Dan's home turf — the email body list is fine for a glance,
// but the deliverable is a spreadsheet. Rows in bookkeeper vocabulary;
// amounts as plain numbers so Excel can sum/filter. Dismissed lines are
// omitted; card payments/credits ride along labeled so the file
// reconciles to the statement total.
//
// input: [{ statement, lines }] — one entry per statement (a single
// statement, or every account in a month). refs: { receipts,
// purchaseOrders }.
export var DAN_SPREADSHEET_HEADERS = [
  'Account', 'Date', 'Charge', 'Amount',
  'What it is', 'PO number', 'Project', 'Receipt in Gilda', 'Documents', 'Notes',
];

export function buildDanSpreadsheetRows(statementsWithLines, refs) {
  var receipts = (refs && refs.receipts) || [];
  var pos      = (refs && refs.purchaseOrders) || [];
  var rById = {}, pById = {};
  receipts.forEach(function(r) { if (r && r.id) rById[r.id] = r; });
  pos.forEach(function(p) { if (p && p.id) pById[p.id] = p; });
  // Incoming credits resolve against recorded client payments when the
  // caller provides the ledger — the Documents column then names the
  // DEP/FIN refs the deposit paid (2026-07-19).
  var clientPayments = (refs && refs.clientPayments) || [];
  var allocations    = (refs && refs.paymentAllocations) || [];
  var invoices       = (refs && refs.invoices) || [];
  var payById = {};
  clientPayments.forEach(function(cp) { if (cp && cp.id) payById[cp.id] = cp; });

  // Sort group order mirrors the email list: project first, then the
  // firm buckets in CARD_LINE_CATEGORIES order, unsorted charges,
  // credits last. Both derive from the category list so new buckets
  // are a one-line add.
  //
  // Matched lines classify by their RECEIPT'S KIND, not blanket
  // "Project purchase" — reimbursable and Office expenses go through
  // the receipt reader too (Cimon 2026-07-12: "reimbursable expenses
  // go through the receipt reader — always... and so do a lot of
  // office expenses"), so a matched charge can be reimbursable or
  // office, not just procurement.
  function keyIndex(key) {
    return CARD_LINE_CATEGORIES.findIndex(function(c) { return c.key === key; });
  }
  function effectiveKey(l, rc) {
    if (l.matchStatus === 'matched' && rc) {
      if (rc.kind === 'project_reimbursable') return 'reimbursable';
      if (rc.kind === 'personal') return 'office';   // receipt kind 'personal' = the Office kind
      return null;   // vendor-kind receipt → project purchase
    }
    if (l.matchStatus === 'matched' || l.poId) return null;   // project purchase
    return l.category || '';
  }
  function groupRank(l, rc) {
    if (l.matchStatus === 'credit') return 999;
    var key = effectiveKey(l, rc);
    if (key === null) return 0;                       // project purchase
    var i = keyIndex(key);
    return i >= 0 ? 1 + i : 900;
  }
  function whatItIs(l, rc) {
    if (l.matchStatus === 'credit') return 'Card payment / credit';
    var key = effectiveKey(l, rc);
    if (key === null) return 'Project purchase';
    var i = keyIndex(key);
    return i >= 0 ? CARD_LINE_CATEGORIES[i].label : 'Not yet sorted';
  }

  var rows = [];
  (statementsWithLines || []).forEach(function(sw) {
    var stmt = (sw && sw.statement) || {};
    var swLines = (sw && sw.lines) || [];
    // Deposit resolution per statement — greedy like the UI, so the
    // spreadsheet agrees with the Incoming money section.
    var creditLines = swLines.filter(function(l) { return l && l.matchStatus === 'credit'; });
    var depositByLine = {};
    if (creditLines.length && clientPayments.length) {
      var dm = matchIncomingDeposits(creditLines, clientPayments);
      creditLines.forEach(function(l, i) { depositByLine[swLines.indexOf(l)] = dm[i]; });
    }
    swLines.forEach(function(l, li) {
      if (!l || l.matchStatus === 'dismissed') return;
      var po = l.poId ? pById[l.poId] : null;
      var rc = l.matchedReceiptId ? rById[l.matchedReceiptId] : null;
      var what = whatItIs(l, rc);
      var docs = '';
      var dep = depositByLine[li];
      if (dep && dep.status === 'recorded') {
        var pay = payById[dep.paymentId];
        what = 'Client deposit \u2014 recorded' + (pay && pay.projectLabel ? ' (' + pay.projectLabel + ')' : '');
        docs = pay ? describeRecordedPaymentDocs(pay, allocations, invoices) : '';
      } else if (dep && dep.status === 'unrecorded') {
        what = 'Incoming \u2014 NOT recorded in Gilda yet';
      }
      rows.push({
        rank: groupRank(l, rc),
        cells: [
          stmt.label || '',
          l.lineDate || '',
          l.descriptor || '',
          Math.round((parseFloat(l.amount) || 0) * 100) / 100,
          what,
          po ? (po.po_ref || po.poRef || '') : '',
          po ? (po.project || '') : '',
          (l.matchStatus === 'matched') ? (rc ? 'yes' : 'receipt deleted')
            : (l.poId ? 'not yet'
            : (l.category === 'reimbursable' ? 'NEEDED \u2014 reimbursables bill through receipts' : '')),
          docs,
          l.notes || '',
        ],
      });
    });
  });
  rows.sort(function(a, b) {
    if (a.rank !== b.rank) return a.rank - b.rank;
    return String(a.cells[1]).localeCompare(String(b.cells[1]));
  });
  return [DAN_SPREADSHEET_HEADERS].concat(rows.map(function(r) { return r.cells; }));
}

// rows (array of cell arrays) → CSV text. UTF-8 BOM so Excel reads
// accents/dashes correctly; quotes escaped per RFC 4180.
export function spreadsheetRowsToCsv(rows) {
  function cell(v) {
    var s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }
  return '﻿' + (rows || []).map(function(r) {
    return r.map(cell).join(',');
  }).join('\r\n');
}

// The reverse pile: receipts dated inside the statement's window (± the
// date tolerance) that no charge matched. "Paid another way, or the
// amounts differ." Derived live — never persisted — so it always
// reflects current receipts.
export function receiptsWithoutCharge(statementLines, receipts, opts) {
  var pad = (opts && opts.padDays) != null ? opts.padDays : DATE_WINDOW_DAYS;
  var dates = (statementLines || [])
    .map(function(l) { return l && (l.lineDate || l.line_date); })
    .filter(Boolean).sort();
  if (!dates.length) return [];
  var lo = addDaysIsoLocal(dates[0], -pad);
  var hi = addDaysIsoLocal(dates[dates.length - 1], pad);
  var matchedIds = {};
  (statementLines || []).forEach(function(l) {
    var rid = l && (l.matchedReceiptId || l.matched_receipt_id);
    if (rid) matchedIds[rid] = true;
  });
  return (receipts || []).filter(function(rc) {
    if (!rc || !rc.id || rc.status === 'rejected') return false;
    if (matchedIds[rc.id]) return false;
    var d = rc.receiptDate || '';
    if (!d || d < lo || d > hi) return false;
    return (parseFloat(rc.totalAmount) || 0) > 0;
  }).sort(function(a, b) {
    return String(a.receiptDate).localeCompare(String(b.receiptDate));
  });
}

function addDaysIsoLocal(iso, n) {
  var p = String(iso || '').split('-');
  if (p.length !== 3) return iso;
  var t = Date.UTC(+p[0], +p[1] - 1, +p[2]) + n * 86400000;
  var d = new Date(t);
  return d.getUTCFullYear() + '-'
    + String(d.getUTCMonth() + 1).padStart(2, '0') + '-'
    + String(d.getUTCDate()).padStart(2, '0');
}

// ── Row mappers (snake_case DB ↔ camelCase app) ───────────────────────

export function rowToCardStatement(row) {
  if (!row) return null;
  return {
    id:         row.id,
    period:     row.period      || '',
    label:      row.label       || '',
    filename:   row.filename    || '',
    uploadedBy: row.uploaded_by || '',
    lineCount:  parseInt(row.line_count, 10) || 0,
    createdAt:  row.created_at  || '',
  };
}

export function rowToCardStatementLine(row) {
  if (!row) return null;
  return {
    id:               row.id,
    statementId:      row.statement_id       || '',
    lineDate:         row.line_date          || '',
    descriptor:       row.descriptor         || '',
    amount:           parseFloat(row.amount) || 0,
    matchedReceiptId: row.matched_receipt_id || null,
    poId:             row.po_id              || null,
    matchStatus:      row.match_status       || 'unmatched',
    matchMethod:      row.match_method       || '',
    category:         row.category           || '',
    notes:            row.notes              || '',
  };
}

export function cardStatementLineToRow(l) {
  return {
    statement_id:       l.statementId,
    line_date:          l.lineDate || null,
    descriptor:         l.descriptor || '',
    amount:             l.amount || 0,
    matched_receipt_id: l.matchedReceiptId || null,   // NULL, never '' (FK rule)
    po_id:              l.poId || null,
    match_status:       l.matchStatus || 'unmatched',
    match_method:       l.matchMethod || '',
    category:           l.category || '',
    notes:              l.notes || '',
  };
}
